import { httpClient } from '../httpClient';

export const consentApi = {
  getAll: (params) =>
    httpClient.get('/consent/records', { params }),

  // NOTE: there is NO bare GET /consent/records/<id> route (DR-06fe) — no
  // getById is defined here, and none should be added. 053 T292's deep-link
  // resolution instead calls getAll({ record_id }) — an additive filter on
  // this SAME route (see useConsentRecordDeepLink in hooks/useConsent.js),
  // not a second endpoint. /receipt, /receipt.pdf, /withdraw,
  // /enforce-withdrawal, and DELETE remain the only per-record
  // sub-resources below. DR-22.

  create: (data) =>
    httpClient.post('/consent/records', data),

  // 053 T051 (cluster K, H-96) — the JSON twin of getReceiptPdf below. Same
  // gate (view_consent) and same 404-when-absent contract, but a small JSON
  // body instead of a PDF blob: useConsentExports.handleDownloadReceipt calls
  // this FIRST to confirm a receipt exists before paying for the PDF
  // render/transfer, so a record with no receipt fails on a cheap request
  // rather than a wasted blob fetch.
  getReceipt: (id) =>
    httpClient.get(`/consent/records/${id}/receipt`),

  // DR-22 — per-record ISO/IEC 27560:2023 receipt as a PDF blob. Gated server-
  // side on view_consent (export == read). 404 when no receipt exists; callers
  // must catch and surface a localized "no receipt" toast.
  getReceiptPdf: (id) =>
    httpClient.get(`/consent/records/${id}/receipt.pdf`, { responseType: 'blob' }),

  // DR-16 / R-3 — server-rendered bilingual consent records-list report as a PDF
  // blob. Replaces the client-side jsPDF list export (which cannot render
  // Arabic). Gated server-side on export_reports. `lang` is 'ar'|'en'; the
  // optional filters mirror the on-screen list so the PDF matches what the
  // operator sees. NOTE the report route reads `consent_type` (NOT `type`).
  getRecordsReportPdf: ({ lang = 'en', status, consent_type, search, record_kind } = {}) => {
    const params = { lang };
    if (status && status !== 'all') params.status = status;
    if (consent_type && consent_type !== 'all') params.consent_type = consent_type;
    if (search) params.search = search;
    if (record_kind) params.record_kind = record_kind;
    return httpClient.get('/consent/records/report.pdf', { params, responseType: 'blob' });
  },

  withdraw: (id) =>
    httpClient.post(`/consent/records/${id}/withdraw`),

  // 053 T105 (cluster L, H-104) — the in-place cross-border-transfer
  // remediation action (routes.py:acknowledge_cross_border). Lets a DPO fix
  // an unacknowledged cross-border grant IN PLACE instead of delete+recreate
  // (CREATE-time acknowledgement was already wired via ConsentForm.js).
  // `acknowledged` defaults true server-side when omitted.
  acknowledgeCrossBorder: (id, data = {}) =>
    httpClient.post(`/consent/records/${id}/cross-border-ack`, data),

  delete: (id) =>
    httpClient.delete(`/consent/records/${id}`),

  bulkDelete: (data) =>
    httpClient.post('/consent/records/bulk-delete', data),

  verify: (data) =>
    httpClient.post('/consent/records/verify', data),

  getWebsites: () =>
    httpClient.get('/consent/websites'),

  createWebsite: (data) =>
    httpClient.post('/consent/websites', data),

  scanWebsite: (id) =>
    httpClient.post(`/consent/websites/${id}/scan`),

  getWebsiteCookies: (id) =>
    httpClient.get(`/consent/websites/${id}/cookies`),

  // Issue #172 sub-bug 1 — soft-deactivate (never a hard delete server-side).
  deleteWebsite: (id) =>
    httpClient.delete(`/consent/websites/${id}`),

  // Issue #172 sub-bug 2 — hydrate the banner configurator wizard from a
  // website's saved configuration instead of always starting blank.
  getBannerConfig: (websiteId) =>
    httpClient.get(`/consent/websites/${websiteId}/banner-config`),

  enforceWithdrawal: (recordId, data = {}) =>
    httpClient.post(`/consent/records/${recordId}/enforce-withdrawal`, data),

  // Phase 5 — Banner Publish + Analytics (T133, T133c, T133g)
  publishBanner: (websiteId, bannerConfig) =>
    httpClient.post('/consent/banner/publish', { website_id: websiteId, banner_config: bannerConfig }),

  getBannerAnalytics: (websiteId, windowDays = 30) =>
    httpClient.get('/consent/banner/analytics', { params: { website_id: websiteId, window_days: windowDays } }),

  // GH-175 upgrade report — banners with Google Consent Mode v2 switched on
  // whose purposes carry no signal mapping, so the banner sends 'denied' for
  // all four signals until it is re-published. Read-only (view_consent).
  getGCMMappingGaps: () =>
    httpClient.get('/consent/banner/gcm-mapping-gaps'),

  // Purposes (already in Phase 3 — aliased for Phase 5 wizard)
  getPurposes: (params) =>
    httpClient.get('/consent/purposes', { params }),

  // Link purpose (FR-034-02 / T018)
  linkPurpose: (recordId, data) =>
    httpClient.post(`/consent/records/${recordId}/link-purpose`, data),

  // Purpose Curation queue (T080 / FR-032-P1-17) — backend: GET /consent/purpose-curation/queue
  getCurationQueue: (params) =>
    httpClient.get('/consent/purpose-curation/queue', { params }),

  // DR-05fe — flag a purpose-curation queue row for DPO review. body
  // {quarantine_reason: 1..64}; gated server-side on manage_compliance. Returns
  // the updated queue-row dict (incl. quarantine_reason).
  quarantineCurationRow: (consentId, data) =>
    httpClient.post(`/consent/purpose-curation/${consentId}/quarantine`, data),

  // DR-05fe — promote a free-text consent purpose into a real ProcessingPurpose
  // + ROPA, then link the consent to it. body {name?: ≤255}; gated server-side
  // on manage_compliance. Returns {consent_id, processing_purpose_id,
  // purpose_name, ropa_record_id, subject_identifier_masked}.
  routeCurationRowToRopa: (consentId, data = {}) =>
    httpClient.post(`/consent/purpose-curation/${consentId}/route-to-ropa`, data),

  // Phase 6 — Command Center (FR-032-P2-04)
  getCommandCenter: () =>
    httpClient.get('/consent/command-center'),

  // Column-level linkage coverage — canonical endpoint (FR-039-07 / DELTA-2)
  getCoverage: (sourceId) =>
    httpClient.get('/consent/coverage', { params: sourceId ? { source_id: sourceId } : {} }),

  // Column-level linkage gaps — canonical endpoint (FR-039-08 / DELTA-2)
  getGaps: (params) =>
    httpClient.get('/consent/gaps', { params }),

  // 053 T280 — `getLinkageCoverage` / `getLinkageGaps` were deleted with the
  // backend aliases they wrapped (`/consent/linkage-coverage`,
  // `/consent/linkage-gaps`). Use getCoverage / getGaps if a
  // classification-grain linkage number is ever needed again; the consent
  // requirement register is purpose-grain and lives on /consent/register.

  // 053 T236/T277 — OBSERVED capture health for one website. Every field is a
  // measured "last time this happened"; null means NEVER OBSERVED, and the
  // widget must render it as such rather than as a zero.
  getChannelHealth: (websiteId) =>
    httpClient.get(`/consent/websites/${websiteId}/channel-health`),

  // 053 T220/T225/T226 — per-channel withdrawal-symmetry state. Two fields
  // matter to the withdrawal-duty surface: `outstanding_actions` (the IR 12(4)
  // external-recipient leg, OUTSTANDING_BY_DESIGN and owned by the CONTROLLER)
  // and `citations` (every locus resolved from the grounding store AT DISPLAY
  // TIME). `lang` MUST be threaded into the query key by every caller — the
  // server resolves the Arabic corpus for `lang=ar`, so a key without it
  // serves cached English quotes to an Arabic reader.
  getChannelSymmetry: (websiteId, lang = 'en') =>
    httpClient.get(`/consent/websites/${websiteId}/symmetry`, { params: { lang } }),

  // Task 7/10 — the same-origin integration pack. `address` is a PRINT-TIME
  // override: it changes the address on this generation of the sheet and does
  // NOT write CONSENT_WIDGET_API_BASE_URL, which is read once at boot. The
  // response is always 200; an unset address and a relay-topology site come
  // back as `available: false` with a reason, because both are states and not
  // errors. The printable rendering is the same route with `format=html` and is
  // opened in a new tab, never fetched into the app.
  getIntegrationPack: (websiteId, { address } = {}) =>
    httpClient.get(`/consent/websites/${websiteId}/integration-pack`,
      address ? { params: { address } } : undefined),

  // Task 13 — opt a website in or out of same-origin serving (OD-6: per
  // website, never auto-flipped on upgrade). Pass null to clear. A
  // relay-topology site is refused 422 SERVING_MODE_REQUIRES_DIRECT_TOPOLOGY
  // by the same service invariant that guards publish and create.
  setServingMode: (websiteId, servingMode) =>
    httpClient.post(`/consent/websites/${websiteId}/serving-mode`,
      { serving_mode: servingMode }),

  // ─── 044 Consent Subject-First — DPO identity queue (US3) ──────────────────
  // GET dpo-queue: list_response {"data":[...],"pagination":{...}}; gated dpo_oversight.
  getIdentityDpoQueue: (params) =>
    httpClient.get('/consent/identity/dpo-queue', { params }),

  // POST confirm: success_response; body {legal_basis, note?}. Gated manage_consent_identity.
  confirmIdentityLink: (linkId, data) =>
    httpClient.post(`/consent/identity/links/${linkId}/confirm`, data),

  // POST unlink: success_response; body {reason?}. Gated manage_consent_identity.
  unlinkIdentity: (linkId, data = {}) =>
    httpClient.post(`/consent/identity/links/${linkId}/unlink`, data),

  // POST merge: success_response; body {anchor_hash_a, anchor_hash_b, legal_basis, linked_ropa_id?, linked_dpia_id, note?}.
  mergeSubjects: (data) =>
    httpClient.post('/consent/identity/merge', data),

  // POST quarantine resolve: success_response; body {resolution, keep_anchor_hash?, legal_basis?, note?}.
  resolveQuarantine: (linkId, data) =>
    httpClient.post(`/consent/identity/quarantine/${linkId}/resolve`, data),

  // ─── 053 T097 — Requirement Register + Exemption Claims (Pillar B/US1-US2) ──
  // contracts/register-exemptions.yaml. Fail-closed list: `params` NARROW the
  // view only (disposition/explicit_consent_tier/notice_coverage/
  // basis_agreement/search/page/per_page) — the unfiltered default is every
  // row. Gated view_consent.
  getRequirementRegister: (params) =>
    httpClient.get('/consent/register', { params }),

  // One register row, purpose grain, full dual-basis + coverage detail.
  // 404 when the purpose does not exist or is archived (the row RETIRES
  // rather than persisting fail-closed forever). Gated view_consent.
  getRequirementRegisterRow: (purposeId) =>
    httpClient.get(`/consent/register/${purposeId}`),

  // Regulator-ready register export (FR-053-50) — DISTINCT from
  // getAttestationsExport (attestations.yaml, IR Art. 33(4)). `format`:
  // 'json' | 'csv'; `disposition` narrows the same way as the list. Gated
  // view_consent.
  getRequirementRegisterExport: ({ format = 'json', disposition } = {}) => {
    const params = { format };
    if (disposition) params.disposition = disposition;
    return httpClient.get('/consent/register/export', {
      params,
      ...(format === 'csv' ? { responseType: 'blob' } : {}),
    });
  },

  // 053 T101 — the minors/incapacity tri-state ANSWER path (T100's register
  // row detail). `triState`: 'applies' | 'does_not_apply' | 'not_declared'
  // — the same three-value vocabulary the register row already renders.
  // Gated manage_compliance (mirrors linkPurpose's ProcessingPurpose-write
  // permission).
  updatePurposeMinorsIncapacity: (purposeId, triState) =>
    httpClient.patch(`/consent/register/${purposeId}/minors-incapacity`,
      { tri_state: triState }),

  // 053 T254 — the notice-coverage CONFIRMATION path (FR-053-38). `noticeId`
  // is a published notice that discloses this purpose; `null` withdraws the
  // confirmation and returns the row to `uncovered`. Gated manage_compliance,
  // same as the minors write above — both land on ProcessingPurpose through
  // the consent module's write allowlist.
  //
  // Coverage is a DPO REVIEW, never "a published notice exists": nothing
  // server-side promotes a row on a publish event (FR-053-38 / T092), so this
  // is the only way the register ever reads `covered`.
  updatePurposeNoticeCoverage: (purposeId, noticeId) =>
    httpClient.patch(`/consent/register/${purposeId}/notice-coverage`,
      { notice_id: noticeId }),

  // Submit half of the four-eyes exemption lifecycle (FR-053-08/09). Ground
  // eligibility (public-entity / sensitive-data / two-part evidence) is
  // The four Art. 6 grounds, verbatim from the grounding store, with the
  // server-computed eligibility posture for this org (+ optional purpose_id
  // for the live 16(1)(c) sensitivity leg). The exemption flow's opening
  // read (FR-053-08, US2 AC1). Gated view_consent.
  getExemptionGrounds: (purposeId) =>
    httpClient.get('/consent/exemptions/grounds',
      { params: purposeId ? { purpose_id: purposeId } : {} }),

  // enforced server-side — 422 on ineligibility, never trust a client-side
  // disabled state. Gated submit_consent_exemption.
  submitExemptionClaim: (data) =>
    httpClient.post('/consent/exemptions', data),

  // List claims (review queues / register cross-linking). `params`:
  // status/purpose_id/ground/page/per_page. Gated view_consent.
  getExemptionClaims: (params) =>
    httpClient.get('/consent/exemptions', { params }),

  getExemptionClaim: (exemptionId) =>
    httpClient.get(`/consent/exemptions/${exemptionId}`),

  // Four-eyes approve — refuses same-actor approval (409
  // FOUR_EYES_SELF_APPROVAL) and, for legitimate_interest, an unreviewed LIA
  // (409 LIA_NOT_REVIEWED). Gated approve_consent_exemption.
  approveExemptionClaim: (exemptionId, data = {}) =>
    httpClient.post(`/consent/exemptions/${exemptionId}/approve`, data),

  // Reject — no four-eyes restriction (the purpose stays IN the register
  // either way); requires a non-empty rejection_reason. Same permission as
  // approve (the other half of the review duty, not a separate grant).
  rejectExemptionClaim: (exemptionId, data) =>
    httpClient.post(`/consent/exemptions/${exemptionId}/reject`, data),

  // Revoke an APPROVED exemption — the operator exit `exempted` never had.
  // Before it, the only writer of `revoked` was the machine path (a
  // classification event, legitimate_interest only), so an exemption approved
  // on any other ground could never be withdrawn and its purpose stayed out of
  // the open consent population permanently. Like reject: no four-eyes
  // restriction (revoking RESTORES the obligation), same
  // approve_consent_exemption permission, and a non-empty revocation_reason is
  // required. 409 CLAIM_NOT_APPROVED for any non-approved state.
  revokeExemptionClaim: (exemptionId, data) =>
    httpClient.post(`/consent/exemptions/${exemptionId}/revoke`, data),

  // ─── 053 T139 — Attestations (Pillar C / US3, contracts/attestations.yaml) ──
  // An attestation is a LABELED HUMAN CLAIM (author • real timestamp •
  // evidence reference), never a verification verdict — no surface reading
  // these responses may render a success idiom (FR-053-13, DNA §3.21a).
  //
  // Refusals the create path can return (422): EVIDENCE_UNSTRUCTURED (a bare
  // free-text note is never sufficient sole evidence), WITHDRAWAL_PATH_MISSING,
  // EXPLICIT_CONSENT_REQUIRES_PROVABLE_RECORDS (⚖️ condition 4 — the external
  // route needs all four pointer elements AND a second, named person who is
  // not the author), MISSING_CREDIT_DISCLOSURE. Gated manage_consent.
  createAttestation: (data) =>
    httpClient.post('/consent/attestations', data),

  // `params`: purpose_id / status ('active'|'expired'|'superseded') /
  // is_exception / page / per_page. Gated view_consent.
  getAttestations: (params) =>
    httpClient.get('/consent/attestations', { params }),

  getAttestation: (attestationId) =>
    httpClient.get(`/consent/attestations/${attestationId}`),

  // Renew — a NEW row superseding the prior; the prior is NEVER edited or
  // re-graded. 409 ATTESTATION_NOT_RENEWABLE when already superseded or
  // exception-closed. Gated manage_consent.
  renewAttestation: (attestationId, data = {}) =>
    httpClient.post(`/consent/attestations/${attestationId}/renew`, data),

  // Attested-with-exception. A near-duplicate reason still SUCCEEDS and comes
  // back with `near_duplicate_of` — a review signal, never a block.
  recordAttestationException: (attestationId, reason) =>
    httpClient.post(`/consent/attestations/${attestationId}/exception`, { reason }),

  // The narrower IR Art. 33(4) attestation-only export — DISTINCT from
  // getRequirementRegisterExport (FR-053-50). `format`: 'json' | 'csv'.
  getAttestationsExport: ({ format = 'json', purposeId } = {}) => {
    const params = { format };
    if (purposeId) params.purpose_id = purposeId;
    return httpClient.get('/consent/attestations/export', {
      params,
      ...(format === 'csv' ? { responseType: 'blob' } : {}),
    });
  },

  // T133 (cluster BB) — the retrievable evidence chain an attestation's
  // evidence reference points at. Gated view_consent.
  getPurposeBindingEvidence: (purposeId) =>
    httpClient.get(`/consent/purposes/${purposeId}/binding-evidence`),

  // ─── 053 T041 — Sole-DPO declaration (routes_sole_dpo.py) ──────────────────
  // Owner ruling 2026-08-21: a sole DPO may stand in as their own second
  // attester ONLY under a declaration a Super Admin recorded naming them.
  // GET is gated view_consent (an auditor must be able to see the declaration
  // behind a self-signed marker); POST/DELETE are gated declare_sole_dpo,
  // Super-Admin-only by design — granting it to the DPO role would let a
  // sole DPO mint their own exception. GET returns `data: null` when none is
  // in force; that is an answer, never a 404.
  getSoleDpoDeclaration: () =>
    httpClient.get('/consent/sole-dpo-declaration'),

  declareSoleDpo: (data) =>
    httpClient.post('/consent/sole-dpo-declaration', data),

  // Revoke — never erase; every artefact already signed under a revoked
  // declaration keeps its marker (revocation changes what may happen next,
  // never what the audit trail says happened). `reason` is optional; the
  // route reads the body `silent=True` so a bodyless DELETE is valid.
  revokeSoleDpoDeclaration: (reason) =>
    httpClient.delete('/consent/sole-dpo-declaration', reason ? { data: { reason } } : undefined),
};
